/**
 * Typography Components
 *
 * This file defines customizable typography components using React and class-variance-authority.
 * It provides a set of components for consistent text styling across the application.
 *
 * Components:
 * - Title: For headings and titles
 * - Paragraph: For body text
 * - Caption: For smaller, supplementary text
 *
 * Each component uses the `cn` utility function from "@/lib/utils" for
 * conditional class name generation and `cva` for managing style variants.
 *
 * Usage:
 * ```jsx
 * import { Title, Paragraph, Caption } from "@/components/ui/typography"
 *
 * <Title size="xl">Main Heading</Title>
 * <Paragraph variant="medium">Body text goes here.</Paragraph>
 * <Caption size="xs" variant="light">Small print</Caption>
 * ```
 *
 * The components use predefined style variants that can be customized
 * through props, allowing for flexible and consistent typography across the app.
 */

import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";

const titleVariants = cva("wireless-Regular text-midnightIndigo-400 tracking-[-0.04px]", {
  variants: {
    size: {
      xxxl: "text-[40px] md:text-[40px] lg:text-[60px] ",
      xxl: "text-[40px] md:text-[40px] lg:text-[52px]",
      xl: "text-[32px] md:text-[36px] lg:text-[40px]",
      lg: "text-[24px] md:text-[26px] lg:text-[32px]",
      md: "text-[20px] md:text-[20px] lg:text-[24px]",
      sm: "text-[16px] md:text-[18px] lg:text-[20px]",
      xs: "text-[16px] md:text-[16px] lg:text-[17px]",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const paragraphVariants = cva("text-[14px] md:text-[15px] lg:text-[16px]  ", {
  variants: {
    variant: {
      regular: "wireless-Regular",
      medium: "wireless-Medium",
      light: "wireless-Light",
    },
  },
  defaultVariants: {
    variant: "regular",
  },
});

const captionVariants = cva("text-midnightIndigo-350 tracking-wide ", {
  variants: {
    variant: {
      regular: "wireless-Regular",
      medium: "wireless-Medium",
      light: "wireless-Light",
    },
    size: {
      xs: "text-[12px] sm:text-[14px] md:text-[13px]",
      sm: "text-[14px] sm:text-[15px] ",
    },
  },
  defaultVariants: {
    variant: "regular",
    size: "sm",
  },
});

const headingVariants = cva("wireless-Medium  text-midnightIndigo-400 tracking-[-0.04px]", {
  variants: {
    variant: {
      h1: "lg:text-[40px] md:text-[36px] text-[32px]",
      h2: "lg:text-[32px] md:text-[26px] text-[24px]",
      h3: "lg:text-[24px] md:text-[20px] text-[20px]",
      h4: "lg:text-[20px] md:text-[28px] text-[18px]",
      h5: "lg:text-[17px] md:text-[16px] text-[16px]",
      h6: "lg:text-[16px] md:text-[15px] text-[14px] wireless-Regular",
    },
  },
  defaultVariants: {
    variant: "h1",
  },
});

interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  a?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  className?: string;
  variant?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  size?: "xxxl" | "xxl" | "xl" | "lg" | "md" | "sm" | "xs";
}

interface TitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  className?: string;
  size?: "xxxl" | "xxl" | "xl" | "lg" | "md" | "sm" | "xs";
}

interface ParagraphProps extends React.HTMLAttributes<HTMLParagraphElement> {
  as?: "p" | "span" | "div";
  className?: string;
  variant?: "regular" | "medium" | "light";
}

interface CaptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  as?: "p" | "span" | "div";
  className?: string;
  variant?: "regular" | "medium" | "light";
  size?: "xs" | "sm";
}

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ a = "h1", className, variant, size, ...props }, ref) => {
    const Component = a;
    // @ts-expect-error size is a valid prop for headingVariants
    return <Component ref={ref} className={cn(headingVariants({ variant, size, className }))} {...props} />;
  }
);

export const Title = React.forwardRef<HTMLHeadingElement, TitleProps>(
  ({ as = "h1", className, size, ...props }, ref) => {
    const Component = as;
    return <Component ref={ref} className={cn(titleVariants({ size, className }))} {...props} />;
  }
);

export const Paragraph = React.forwardRef<HTMLParagraphElement, ParagraphProps>(
  ({ as = "p", className, variant, ...props }, ref) => {
    const Component = as;
    return <Component ref={ref} className={cn(paragraphVariants({ variant, className }))} {...props} />;
  }
);

export const Caption = React.forwardRef<HTMLParagraphElement, CaptionProps>(
  ({ as = "p", className, variant, size, ...props }, ref) => {
    const Component = as;
    return <Component ref={ref} className={cn(captionVariants({ variant, size, className }))} {...props} />;
  }
);

Heading.displayName = "Heading";
Title.displayName = "Title";
Paragraph.displayName = "Paragraph";
Caption.displayName = "Caption";
